import {
  Box,
  IconCalendarDays,
  PopoverWrapper,
  Skeleton
} from '@sequencehq/core-components'
import { BillingSchedulePreviewCard } from 'BillingSchedules/index'
import { useCubeContext } from 'Cube/communication/internal/cube.domain.context'
import { formatScheduleDateRange } from 'Cube/utils/formatScheduleDateRange'
import { useAmendedSchedule } from 'Cube/view/layouts/quote/QuoteEditorControls/AmendmentEffectiveFrom/useAmendedSchedule'
import { SidebarField } from 'Cube/view/layouts/quote/QuoteEditorSidebar/widgets/SidebarField'
import { SidebarPill } from 'Cube/view/layouts/quote/QuoteEditorSidebar/widgets/SidebarPill'
import { useState } from 'react'
import { useDebouncedCallback } from 'use-debounce'

const PREVIEW_CARD_WIDTH = '320px'
const OPEN_DELAY = 850
const CLOSE_DELAY = 200

/**
 * Names the schedule an amendment quote was built from. Read only: hovering opens the schedule's
 * preview card, and the link through to it lives on that card rather than on the pill. Clicking
 * the pill opens the card there and then, for anyone who doesn't wait out the hover delay.
 *
 * Renders nothing when there is no schedule to name - a quote that isn't an amendment, or one
 * whose schedule we couldn't read. The row's whole content is the schedule's dates, so there is
 * nothing to show without them, and the effective-date control already reports a schedule that
 * failed to load.
 */
export const AmendedScheduleField = () => {
  const ctx = useCubeContext()
  const scheduleId = ctx.queries.rawData.data.quote.amendment?.billingScheduleId
  const { schedule, isLoading } = useAmendedSchedule()
  const previewCard = usePreviewCardState()

  if (!scheduleId) {
    return null
  }

  if (isLoading) {
    return (
      <SidebarField
        label="Schedule"
        control={<Skeleton width="150px" height="24px" borderRadius="6px" />}
      />
    )
  }

  if (!schedule) {
    return null
  }

  return (
    <SidebarField
      label="Schedule"
      control={
        <PopoverWrapper
          popover={
            <Box
              width={PREVIEW_CARD_WIDTH}
              onMouseEnter={previewCard.onPointerOver}
              onMouseLeave={previewCard.onPointerOut}
            >
              {/* The popover draws its own border, so the card must not draw a second one. */}
              <BillingSchedulePreviewCard
                scheduleId={scheduleId}
                showBorder={false}
              />
            </Box>
          }
          // Hung off the pill's right edge, so a card wider than the pill grows into the sidebar
          // rather than off the side of it.
          placement="bottom-end"
          offset={[0, 4]}
          isOpen={previewCard.isOpen}
        >
          <Box
            onMouseEnter={previewCard.onPointerOver}
            onMouseLeave={previewCard.onPointerOut}
            onClick={previewCard.openNow}
          >
            <SidebarPill
              icon={<IconCalendarDays />}
              data-testid="quote.amendedScheduleControl"
            >
              {formatScheduleDateRange(schedule)}
            </SidebarPill>
          </Box>
        </PopoverWrapper>
      }
    />
  )
}

/**
 * Whether the preview card is open. It opens after a pause on the pill, or straight away on a
 * click, and the pill and the card count as one area, so the pointer can wander between them
 * freely. Leaving waits a moment, because the pointer is over neither while it crosses the gap:
 *
 *   [ pill ]
 *    ......   <- the gap, which no handler here sees
 *   +------+
 *   | card |
 *   +------+
 *
 * The popover's own hover handling can't do this - a click forces it open, and handing it back
 * closed the card as the pointer left.
 */
const usePreviewCardState = () => {
  const [isOpen, setIsOpen] = useState(false)

  const openAfterPause = useDebouncedCallback(() => setIsOpen(true), OPEN_DELAY)
  const closeAfterPause = useDebouncedCallback(
    () => setIsOpen(false),
    CLOSE_DELAY
  )

  return {
    isOpen,
    onPointerOver: () => {
      closeAfterPause.cancel()
      openAfterPause()
    },
    onPointerOut: () => {
      openAfterPause.cancel()
      closeAfterPause()
    },
    openNow: () => {
      openAfterPause.cancel()
      closeAfterPause.cancel()
      setIsOpen(true)
    }
  }
}
